import {
  StarBorderRounded,
  Add,
  AppsRounded,
  QrCodeScanner,
  CameraAlt,
  Close,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogContent, IconButton, TextField, Typography } from "@mui/material";
import styles from "./HomePanel.module.css";
import { useGetAllTopCategoriesQuery } from "../../Store/Services/CategoryService";
import { useGetAllProductsQuery } from "../../Store/Services/ProductService";
import { useLazyGetAllSearchedProductsQuery } from "../../Store/Services/ProductService";
import { useLazyGetSearchedProductsByCodeQuery } from "../../Store/Services/ProductService";
import { useSelector } from "react-redux";
import { useAddToCartMutation } from "../../Store/Services/CartService";
import { toast } from "react-toastify";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { getGlobalCartId, setGlobalCartId } from "../../Utils/cartSession";

const HomePanel = ({ selectedItems = [] }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [codeSearchResults, setCodeSearchResults] = useState([]);
  const [isCodeSearchActive, setIsCodeSearchActive] = useState(false);
  const [lastCodeSearchValue, setLastCodeSearchValue] = useState("");
  const [codeSearchEmptyMessage, setCodeSearchEmptyMessage] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const scanInputRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraLoopRef = useRef(null);
  const cameraDecoderRef = useRef(null);
  const scannerActiveRef = useRef(false);

  const currency = useSelector((state) => state.currency);

  const [fetchProductsByCategory, { data: productsData }] =
    useLazyGetAllSearchedProductsQuery();
  const [fetchProductsByCode] = useLazyGetSearchedProductsByCodeQuery();

  const handleCategoryClick = (category) => {
    const selectedCategoryValue = String(
      category?.prodCatUuid ?? category?.prodCatUUID ?? category?.catId ?? ""
    ).trim();

    setIsCodeSearchActive(false);
    setLastCodeSearchValue("");
    setCodeSearchEmptyMessage("");
    setCodeSearchResults([]);
    setSelectedCategory(selectedCategoryValue || null);
    if (selectedCategoryValue) {
      fetchProductsByCategory(
        `?Category=${encodeURIComponent(
          selectedCategoryValue
        )}&pageNumber=1&pageSize=50`
      );
    }
  };

  const { data: categories, isSuccess: categoriesSuccess } =
    useGetAllTopCategoriesQuery();
  const { data: items } = useGetAllProductsQuery();
  const categoriesList = categories?.list || [];
  const itemsList = items?.list || [];
  const image = `${process.env.REACT_APP_IMAGE_URL}`;

  const [addToCart] = useAddToCartMutation();
  const getList = (list) => (Array.isArray(list) ? list.flat() : []);
  const getUniqueProducts = (list) => {
    const uniqueMap = new Map();

    list.forEach((item) => {
      const key = item?.prodDefUUID ?? item?.prodDefId;
      if (!key || uniqueMap.has(key)) {
        return;
      }
      uniqueMap.set(key, item);
    });

    return Array.from(uniqueMap.values());
  };
  const searchedList = getList(productsData?.list);
  const searchedByCodeList = getUniqueProducts(getList(codeSearchResults));
  const normalizeCategoryValue = (value) => String(value ?? "").trim();
  const fallbackFilteredItems = itemsList.filter((item) => {
    const itemCategoryValue = normalizeCategoryValue(
      item?.prodCatUuid ??
        item?.prodCatUUID ??
        item?.category?.prodCatUuid ??
        item?.category?.prodCatUUID ??
        item?.catId ??
        item?.categoryId ??
        item?.topCatId ??
        item?.category?.catId
    );
    return selectedCategory ? itemCategoryValue === selectedCategory : true;
  });

  const categoryVisibleItems = searchedList.length ? searchedList : fallbackFilteredItems;
  const visibleItems = isCodeSearchActive
    ? searchedByCodeList
    : selectedCategory
      ? categoryVisibleItems
      : itemsList;

  const showNoItemsMessage =
    (isCodeSearchActive && searchedByCodeList.length === 0) ||
    (!isCodeSearchActive && Boolean(selectedCategory) && categoryVisibleItems.length === 0);

  const noItemsMessage = isCodeSearchActive
    ? codeSearchEmptyMessage || `There are no items for code: ${lastCodeSearchValue}.`
    : "There are no items for this category.";

  const normalizeCode = (value) => String(value || "").trim().toLowerCase();

  const extractCandidateCodes = useCallback(
    (rawCode) => {
      const raw = String(rawCode || "").trim();
      if (!raw) {
        return [];
      }

      const candidates = new Set([raw]);

      // Support scanners that append multiple payload fragments in one string.
      raw
        .split(/[|,;\n\r]/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => candidates.add(part));

      // Support QR payloads like URLs with code values in query or path.
      try {
        const parsed = new URL(raw);
        [
          "code",
          "barcode",
          "barCode",
          "barcodeNo",
          "barCodeNo",
          "qrcode",
          "qrCode",
          "sku",
          "itemCode",
          "productCode",
          "prodCode",
        ].forEach((key) => {
          const value = parsed.searchParams.get(key);
          if (value) {
            candidates.add(value.trim());
          }
        });

        const pathLastSegment = parsed.pathname.split("/").filter(Boolean).pop();
        if (pathLastSegment) {
          candidates.add(pathLastSegment.trim());
        }
      } catch {
        // Payload is not a URL; raw and delimited values are already handled.
      }

      return Array.from(candidates)
        .map((candidate) => normalizeCode(candidate))
        .filter(Boolean);
    },
    []
  );

  const findProductByCode = useCallback(
    (rawCode) => {
      const candidateCodes = extractCandidateCodes(rawCode);
      if (!candidateCodes.length) {
        return null;
      }

      const matcherFields = [
        "barcode",
        "barCode",
        "barCodeNo",
        "qrCode",
        "sku",
        "code",
        "itemCode",
        "productCode",
        "prodCode",
        "serialNo",
        "title",
      ];

      return itemsList.find((item) =>
        matcherFields.some((field) => {
          const itemCode = normalizeCode(item?.[field]);
          return itemCode && candidateCodes.includes(itemCode);
        })
      );
    },
    [extractCandidateCodes, itemsList]
  );

  const handleAddToCart = async (item, source = "manual") => {
    const currentCartId = getGlobalCartId();
    const dataSet = {
      orderType: 2,
      prodDefId: item.prodDefId,
      qty: 1,
      orderHeadId:
        !currentCartId || currentCartId === "undefined" ? 0 : currentCartId,
      prodHeaderId: 0,
    };
    if (item.quantity === 0) {
      toast.error("Out of stock", { position: "bottom-right" });
      return false;
    } else {
      try {
        const userCartId = await addToCart(dataSet).unwrap();
        setGlobalCartId(userCartId.result);
        scanInputRef.current?.focus();
        window.dispatchEvent(
          new CustomEvent("pos:cart-id-changed", {
            detail: { cartId: userCartId.result },
          })
        );
        if (source === "scan") {
          window.dispatchEvent(
            new CustomEvent("pos:scan-add-success", {
              detail: {
                prodDefId: item?.prodDefId,
                title: item?.title,
              },
            })
          );
        }
        return true;
      } catch (err) {
        console.error("Failed to add to cart: ", err);
        toast.error("Error, something went wrong!");
        return false;
      }
    }
  };

  const processScannedCode = useCallback(
    async (rawCode) => {
      const normalized = String(rawCode || "").trim();
      if (!normalized) {
        return;
      }

      setIsCodeSearchActive(true);
      setLastCodeSearchValue(normalized);
      setScanCode(normalized);
      let foundItem = null;

      try {
        const searchedProducts = await fetchProductsByCode(
          `?pageNumber=1&pageSize=50&code=${encodeURIComponent(normalized)}`
        ).unwrap();
        const apiMatchedItems = getUniqueProducts(getList(searchedProducts?.list));
        setCodeSearchResults(apiMatchedItems);

        if (!apiMatchedItems.length) {
          const responseMessage = String(searchedProducts?.statusMessage || "").trim();
          setCodeSearchEmptyMessage(
            responseMessage && responseMessage.toLowerCase() !== "ok"
              ? responseMessage
              : `There are no items for code: ${normalized}.`
          );
        } else {
          setCodeSearchEmptyMessage("");
        }

        foundItem = apiMatchedItems.find((item) => {
          const candidateCodes = extractCandidateCodes(normalized);
          return candidateCodes.some((code) =>
            [item?.barcode, item?.barCode, item?.barCodeNo, item?.qrCode, item?.sku, item?.code]
              .map((value) => normalizeCode(value))
              .includes(code)
          );
        }) || apiMatchedItems[0] || null;
      } catch (error) {
        console.error("Failed to search products by code:", error);
        setCodeSearchResults([]);
        setCodeSearchEmptyMessage("Unable to search items for this code. Please try again.");
      }

      if (!foundItem) {
        foundItem = findProductByCode(normalized);
      }

      if (!foundItem) {
        toast.error(`No product found for code: ${normalized}`, {
          position: "bottom-right",
        });
        scanInputRef.current?.focus();
        return;
      }

      const isAdded = await handleAddToCart(foundItem, "scan");
      if (isAdded) {
        setScanCode("");
      }
      scanInputRef.current?.focus();
    },
    [extractCandidateCodes, fetchProductsByCode, findProductByCode, getList]
  );

  useEffect(() => {
    scanInputRef.current?.focus();
  }, []);

  const stopCameraScanner = useCallback(() => {
    scannerActiveRef.current = false;

    if (cameraLoopRef.current) {
      cancelAnimationFrame(cameraLoopRef.current);
      cameraLoopRef.current = null;
    }

    if (cameraDecoderRef.current) {
      try {
        cameraDecoderRef.current.reset();
      } catch {
        // Ignore decoder cleanup issues.
      }
      cameraDecoderRef.current = null;
    }

    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }

    if (videoRef.current) {
      try {
        const mediaStream = videoRef.current.srcObject;
        if (mediaStream && typeof mediaStream.getTracks === "function") {
          mediaStream.getTracks().forEach((track) => track.stop());
        }
      } catch {
        // Ignore video cleanup issues.
      }
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
  }, []);

  const handleCloseScanner = useCallback(() => {
    setCameraOpen(false);
    stopCameraScanner();
  }, [stopCameraScanner]);

  const startCameraScanner = useCallback(async () => {
    try {
      scannerActiveRef.current = true;
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      if (!scannerActiveRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      cameraStreamRef.current = stream;

      if (!videoRef.current) {
        return;
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({
          formats: ["qr_code", "code_128", "ean_13", "ean_8", "upc_a", "upc_e"],
        });

        const scanFrame = async () => {
          if (!scannerActiveRef.current || !videoRef.current) {
            return;
          }

          try {
            if (videoRef.current.readyState >= 2) {
              const detections = await detector.detect(videoRef.current);
              if (detections?.length) {
                const detectedCode = detections[0].rawValue;
                if (detectedCode) {
                  handleCloseScanner();
                  await processScannedCode(detectedCode);
                  return;
                }
              }
            }
          } catch (error) {
            setCameraError("Unable to detect code. Please retry.");
          }

          cameraLoopRef.current = requestAnimationFrame(scanFrame);
        };

        cameraLoopRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      const decoder = new BrowserMultiFormatReader();
      cameraDecoderRef.current = decoder;

      decoder.decodeFromVideoElement(videoRef.current, async (result, error) => {
        if (!scannerActiveRef.current) {
          return;
        }

        if (result) {
          const detectedCode = result.getText();
          if (detectedCode) {
            handleCloseScanner();
            await processScannedCode(detectedCode);
          }
          return;
        }

        if (error && error.name !== "NotFoundException") {
          setCameraError("Unable to detect code. Please retry.");
        }
      });
    } catch (error) {
      const permissionErrors = ["NotAllowedError", "SecurityError", "PermissionDeniedError"];
      const cameraUnavailableErrors = ["NotFoundError", "OverconstrainedError"];

      if (permissionErrors.includes(error?.name)) {
        setCameraError("Camera permission denied. Please allow camera access and try again.");
      } else if (cameraUnavailableErrors.includes(error?.name)) {
        setCameraError("No compatible camera found on this device.");
      } else {
        setCameraError("Unable to start camera scanner. Please try again.");
      }
      stopCameraScanner();
    }
  }, [handleCloseScanner, processScannedCode, stopCameraScanner]);

  useEffect(() => {
    if (cameraOpen) {
      startCameraScanner();
    } else {
      stopCameraScanner();
    }

    return () => {
      stopCameraScanner();
    };
  }, [cameraOpen, startCameraScanner, stopCameraScanner]);

  useEffect(() => {
    let scanBuffer = "";
    let lastKeyTime = 0;
    let clearBufferTimer = null;

    const onKeyDown = (event) => {
      if (cameraOpen) {
        return;
      }

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastKeyTime > 120) {
        scanBuffer = "";
      }
      lastKeyTime = now;

      if (event.key === "Enter") {
        if (scanBuffer.length >= 3) {
          processScannedCode(scanBuffer);
        }
        scanBuffer = "";
        return;
      }

      if (event.key.length === 1) {
        scanBuffer += event.key;
        clearTimeout(clearBufferTimer);
        clearBufferTimer = setTimeout(() => {
          scanBuffer = "";
        }, 250);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearTimeout(clearBufferTimer);
    };
  }, [cameraOpen, processScannedCode]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.categoryRow}>
        <div className={styles.chipScroller}>
          <button
            className={`${styles.categoryChip} ${!selectedCategory ? styles.categoryChipActive : ""}`}
            onClick={() => handleCategoryClick(null)}
          >
            <span className={styles.chipAllIcon}>
              <AppsRounded fontSize="small" />
            </span>
            <span>All</span>
          </button>
          {categoriesSuccess &&
            categoriesList.map((category) => (
              <button
                key={category.prodCatUuid ?? category.catId}
                className={`${styles.categoryChip} ${
                  selectedCategory ===
                  normalizeCategoryValue(category.prodCatUuid ?? category.catId)
                    ? styles.categoryChipActive
                    : ""
                }`}
                onClick={() => handleCategoryClick(category)}
              >
                {category.imagesList && category.imagesList.split(",")[0] ? (
                  <img
                    className={styles.chipImage}
                    src={`${image}${category.imagesList.split(",")[0]}`}
                    alt={category.catName}
                  />
                ) : (
                  <span className={styles.chipAllIcon}>
                    <AppsRounded fontSize="small" />
                  </span>
                )}
                <span>{category.catName}</span>
              </button>
            ))}
        </div>
      </div>

      <div className={styles.scanRow}>
        <TextField
          size="small"
          value={scanCode}
          inputRef={scanInputRef}
          onChange={(event) => {
            const value = event.target.value;
            setScanCode(value);
            if (!String(value || "").trim()) {
              setIsCodeSearchActive(false);
              setLastCodeSearchValue("");
              setCodeSearchEmptyMessage("");
              setCodeSearchResults([]);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              processScannedCode(scanCode);
            }
          }}
          placeholder="Scan or type barcode / QR code"
          className={styles.scanInput}
        />
        <Button
          variant="contained"
          className={styles.scanButton}
          startIcon={<QrCodeScanner />}
          onClick={() => processScannedCode(scanCode)}
        >
          Add by Scan
        </Button>
        <Button
          variant="outlined"
          className={styles.cameraButton}
          startIcon={<CameraAlt />}
          onClick={() => setCameraOpen(true)}
        >
          Use Camera
        </Button>
      </div>

      <div className={styles.itemContainer}>
        {showNoItemsMessage ? (
          <Typography sx={{ width: "100%", textAlign: "center", py: 4 }}>
            {noItemsMessage}
          </Typography>
        ) : (
          visibleItems.map((item) => (
            <div
              key={item.prodDefId}
              className={`${styles.productCard} ${
                item.quantity === 0 ? styles.disabledItem : ""
              }`}
            >
              <div className={styles.productImageWrap}>
                {item.imageList && item.imageList.split(",")[0] && (
                  <img
                    src={`${image}${item.imageList.split(",")[0]}`}
                    alt={item.title}
                  />
                )}
                <button className={styles.starButton} aria-label="Favourite">
                  <StarBorderRounded fontSize="small" />
                </button>
              </div>
              <div className={styles.itemDetails}>
                <Typography className={styles.itemName}>
                  {item.title.length > 30
                    ? `${item.title.substring(0, 30)}...`
                    : item.title}
                </Typography>
                <div className={styles.priceRow}>
                  <Typography className={styles.itemPrice}>
                    {currency.currencySymbol}{" "}
                    {(
                      (Number(item.price) || 0) *
                      (1 - (Number(item.discount) || 0) / 100) *
                      (parseFloat(currency.currencyRate) || 1)
                    ).toFixed(2)}
                  </Typography>
                  <button
                    className={styles.addButton}
                    onClick={() => handleAddToCart(item, "manual-grid")}
                    aria-label="Add to bill"
                  >
                    <Add fontSize="small" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog
        open={cameraOpen}
        onClose={handleCloseScanner}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent className={styles.scannerDialogContent}>
          <div className={styles.scannerDialogHeader}>
            <Typography className={styles.scannerDialogTitle}>Scan Barcode or QR</Typography>
            <IconButton onClick={handleCloseScanner} aria-label="Close scanner">
              <Close />
            </IconButton>
          </div>
          <div className={styles.videoWrap}>
            <video ref={videoRef} className={styles.scannerVideo} muted playsInline />
            <div className={styles.scanOverlay} />
          </div>
          {cameraError ? (
            <Typography className={styles.cameraError}>{cameraError}</Typography>
          ) : (
            <Typography className={styles.scannerHint}>
              Point camera at a barcode or QR code to add item automatically.
            </Typography>
          )}
          <Button
            variant="outlined"
            fullWidth
            className={styles.stopCameraButton}
            onClick={handleCloseScanner}
          >
            Stop Camera
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePanel;
